import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import SideMenu from '../components/layout/SideMenu';
import ViewToggle from '../components/products/ViewToggle';
import StoreCard from '../components/products/StoreCard';
import Alert from '../components/common/Alert';
import Pagination from '../components/common/Pagination';
import { useAllStores } from '../hooks/useAllStores';
import { ViewMode } from '../types/category';
import styles from './AllStoresPage.module.css';
import tableStyles from '../styles/shared/DataTable.module.css';
import cardGridStyles from '../styles/shared/CardGrid.module.css';
import placeholderImage from '../assets/placeholder.png';

const AllStoresPage: React.FC = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [searchInput, setSearchInput] = useState('');
  const {
    isAdmin,
    stores,
    isLoading,
    alert,
    hideAlert,
    handleDelete,
    handleToggleActive,
    pagination,
  } = useAllStores();

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    pagination.setSearch(searchInput.trim());
    pagination.setPage(1);
  };

  const handleAddStore = () => navigate('/create-store');

  if (!isAdmin) {
    return (
      <div className={styles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <div className={styles.errorContainer}>
              <p>Access denied. Only administrators can manage all stores.</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (isLoading && stores.length === 0) {
    return (
      <div className={styles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <p className={styles.loadingMessage}>Loading stores...</p>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <Header />
      <div className={styles.mainLayout}>
        <SideMenu />
        <main className={styles.contentArea}>
          <Alert
            message={alert.message}
            type={alert.type}
            isVisible={alert.isVisible}
            onClose={hideAlert}
          />

          <div className={styles.titleBar}>
            <h2 className={styles.title} title="All Stores">All Stores</h2>
            <div className={styles.controls}>
              <ViewToggle currentView={viewMode} onViewChange={setViewMode} />
              <button onClick={handleAddStore} className={styles.addButton} title="Add Store">
                Add Store
              </button>
            </div>
          </div>

          <div className={styles.toolbar}>
            <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                placeholder="Search stores..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className={styles.searchInput}
              />
              <button type="submit" className={styles.searchButton}>Search</button>
            </form>
            <label className={styles.sortLabel}>
              Sort by:
              <select
                value={`${pagination.sortBy}-${pagination.sortOrder}`}
                onChange={(e) => {
                  const [by, order] = e.target.value.split('-') as [string, 'asc' | 'desc'];
                  pagination.setSortBy(by);
                  pagination.setSortOrder(order);
                  pagination.setPage(1);
                }}
                className={styles.sortSelect}
              >
                <option value="id-desc">ID (newest)</option>
                <option value="id-asc">ID (oldest)</option>
                <option value="store_name-asc">Name (A–Z)</option>
                <option value="store_name-desc">Name (Z–A)</option>
                <option value="is_active-desc">Status (active first)</option>
                <option value="is_active-asc">Status (pending first)</option>
              </select>
            </label>
          </div>

          {stores.length === 0 && !isLoading ? (
            <div className={styles.emptyState}>
              <p title="No stores found. Click Add Store to create one.">
                No stores found. Click &quot;Add Store&quot; to create one.
              </p>
              <button onClick={handleAddStore} className={styles.addButton} title="Add Store">
                Add Store
              </button>
            </div>
          ) : viewMode === 'table' ? (
            <div className={styles.tableContainer}>
              <table className={tableStyles.dataTable}>
                <thead>
                  <tr>
                    <th title="Image">Image</th>
                    <th title="Store Name">Store Name</th>
                    <th title="Description">Description</th>
                    <th title="Email">Email</th>
                    <th title="Mobile">Mobile</th>
                    <th title="Location">Location</th>
                    <th title="Status">Status</th>
                    <th title="Owner">Owner</th>
                    <th title="Actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stores.map((store) => (
                    <tr key={store.id}>
                      <td className={tableStyles.imageCell} title={store.store_name}>
                        {store.image_url ? (
                          <img
                            src={store.image_url}
                            alt={store.store_name}
                            title={store.store_name}
                            className={tableStyles.tableImage}
                            onError={(e) => { e.currentTarget.src = placeholderImage; }}
                          />
                        ) : (
                          <div className={tableStyles.tablePlaceholder} title={store.store_name}>🏪</div>
                        )}
                      </td>
                      <td title={store.store_name}>{store.store_name}</td>
                      <td className={styles.descriptionCell} title={store.store_description}>
                        {store.store_description}
                      </td>
                      <td title={store.email}>{store.email}</td>
                      <td title={store.mobile_number || 'N/A'}>{store.mobile_number || 'N/A'}</td>
                      <td title={store.subLocation?.location?.location_name && store.subLocation?.sub_location_name ? `${store.subLocation.location.location_name} - ${store.subLocation.sub_location_name}` : 'N/A'}>
                        {store.subLocation?.location?.location_name && store.subLocation?.sub_location_name
                          ? `${store.subLocation.location.location_name} - ${store.subLocation.sub_location_name}`
                          : 'N/A'}
                      </td>
                      <td>
                        <span
                          className={`${styles.statusBadge} ${store.is_active ? styles.statusActive : styles.statusPending}`}
                          title={store.is_active ? 'Active' : 'Pending'}
                        >
                          {store.is_active ? 'Active' : 'Pending'}
                        </span>
                      </td>
                      <td title={store.user ? `${store.user.first_name || ''} ${store.user.last_name || ''}`.trim() || store.user.username : 'N/A'}>
                        {store.user
                          ? `${store.user.first_name || ''} ${store.user.last_name || ''}`.trim() || store.user.username
                          : 'N/A'}
                      </td>
                      <td>
                        <div className={styles.actionButtons}>
                          <button
                            onClick={() => handleToggleActive(store)}
                            className={`${styles.actionButton} ${store.is_active ? styles.disableButton : styles.enableButton}`}
                            title={store.is_active ? 'Disable Store' : 'Enable Store'}
                          >
                            {store.is_active ? 'Disable' : 'Enable'}
                          </button>
                          <Link
                            to={`/store/${store.id}/overview`}
                            className={`${styles.actionButton} ${styles.viewButton}`}
                            title="View Store"
                          >
                            View
                          </Link>
                          <Link
                            to={`/store/${store.id}/settings`}
                            className={`${styles.actionButton} ${styles.editButton}`}
                            title="Edit Store"
                          >
                            Edit
                          </Link>
                          <button
                            onClick={() => handleDelete(store.id)}
                            className={`${styles.actionButton} ${styles.deleteButton}`}
                            title="Delete Store"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={cardGridStyles.cardsGrid}>
              {stores.map((store) => (
                <StoreCard key={store.id} store={store} onDelete={handleDelete} onToggleActive={handleToggleActive} />
              ))}
            </div>
          )}
          {pagination.total > 0 && (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              limit={pagination.limit}
              onPageChange={pagination.setPage}
              onLimitChange={(newLimit) => { pagination.setLimit(newLimit); pagination.setPage(1); }}
              limitOptions={[10, 20, 50]}
              showLimitSelector
              disabled={isLoading}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default AllStoresPage;
